import type { UseQueryResult } from "@tanstack/react-query";

import type {
    ProWebsiteControllersApiWidgetAdIntelligenceAdvertiserDailyOverviewResponse,
    ProWebsiteControllersApiWidgetAdIntelligencePublishersOverviewResponse,
} from "@similarweb/pro-api-client";

import { useApiQuery } from "app/modules/api-client";
import type {
    AdIntelligenceController,
    AdIntelligenceControllerAdvertiser,
    AdIntelligenceControllerPublisher,
} from "app/pages/digital-marketing/ad-intelligence/common/shared/types/types";
import { default as DurationService, BasicDurations } from "app/services/DurationService";

import { useDisplayAdvertiserPublisherDataAndParams } from "../../hooks/useDisplayAdvertiserPublisherDataAndParams";
import { isRequestedDateBeforeMay2025 } from "../utils/utils";

export function usePerformanceQuery(
    controller?: undefined,
): UseQueryResult<{ records: Record<string, unknown>; hasAdIntelMetrics: false }>;
export function usePerformanceQuery(
    controller: AdIntelligenceControllerAdvertiser,
): UseQueryResult<ProWebsiteControllersApiWidgetAdIntelligenceAdvertiserDailyOverviewResponse>;
export function usePerformanceQuery(
    controller: AdIntelligenceControllerPublisher,
): UseQueryResult<ProWebsiteControllersApiWidgetAdIntelligencePublishersOverviewResponse>;
export function usePerformanceQuery(
    controller: AdIntelligenceController,
): UseQueryResult<
    | ProWebsiteControllersApiWidgetAdIntelligenceAdvertiserDailyOverviewResponse
    | ProWebsiteControllersApiWidgetAdIntelligencePublishersOverviewResponse
>;
export function usePerformanceQuery(
    controller?: AdIntelligenceController,
): UseQueryResult<
    | { records: Record<string, unknown>; hasAdIntelMetrics: false }
    | ProWebsiteControllersApiWidgetAdIntelligenceAdvertiserDailyOverviewResponse
    | ProWebsiteControllersApiWidgetAdIntelligencePublishersOverviewResponse
> {
    const params = useDisplayAdvertiserPublisherDataAndParams();
    const { from, to, isWindow } = DurationService.getDurationData(params.duration).forAPI;
    // Data is not available before May 2025 so there's no need to even try to fetch it
    const isBeforeMay2025 = isRequestedDateBeforeMay2025(params.duration);

    const requestParams = {
        country: params.country,
        key: params.key.split(","),
        from,
        to,
        isWindow,
        ...(isWindow ? { latest: BasicDurations.LAST_TWENTY_EIGHT_DAYS } : {}),
    };

    const methodName =
        controller === "advertiser"
            ? "adIntelligenceAdvertiserPerformanceOverviewV2UnifiedList"
            : "adIntelligencePublisherPerformanceOverviewV2UnifiedList";

    return useApiQuery(
        {
            tag: "AdIntelligenceMediaPartners",
            methodName,
            options: {
                enabled: controller !== undefined || !isBeforeMay2025,
                select: (response) => {
                    return controller === undefined ? { records: {}, hasAdIntelMetrics: false } : response;
                },
            },
        },
        requestParams,
    ) as UseQueryResult<
        | { records: Record<string, unknown>; hasAdIntelMetrics: false }
        | ProWebsiteControllersApiWidgetAdIntelligenceAdvertiserDailyOverviewResponse
        | ProWebsiteControllersApiWidgetAdIntelligencePublishersOverviewResponse
    >;
}
